// Customizable Area Start
import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@material-ui/core";
import { StepOtpProps } from "./types";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 60;

const StepOtp: React.FC<StepOtpProps> = ({
  otp,
  otpError,
  loading,
  phone,
  onChange,
  onContinue,
  onChangePhone,
  onResendOtp,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>(
    Array(OTP_LENGTH).fill(null)
  );
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SEC);
  const [canResend, setCanResend] = useState(false);
  const [resending, setResending] = useState(false);

  // Reset timer whenever phone changes (new OTP sent)
  useEffect(() => {
    setCountdown(RESEND_COOLDOWN_SEC);
    setCanResend(false);
    setResending(false);
  }, [phone]);

  // Countdown tick
  useEffect(() => {
    if (countdown === 0) {
      setCanResend(true);
      return;
    }
    const id = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [countdown]);

  const handleInput = (value: string, index: number) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    onChange(next);
    // Advance focus — keep keyboard open by moving to next field immediately
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === "Backspace") {
      const next = [...otp];
      if (next[index]) {
        next[index] = "";
        onChange(next);
      } else if (index > 0) {
        next[index - 1] = "";
        onChange(next);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  // Paste support: paste 6 digits at once
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!text) return;
    const next = Array(OTP_LENGTH).fill("");
    text.split("").forEach((char, i) => { next[i] = char; });
    onChange(next);
    const lastFocus = Math.min(text.length, OTP_LENGTH - 1);
    inputRefs.current[lastFocus]?.focus();
  };

  const handleResend = () => {
    setResending(true);
    setCountdown(RESEND_COOLDOWN_SEC);
    setCanResend(false);
    onChange(Array(OTP_LENGTH).fill(""));
    onResendOtp();
    // Re-focus first input after resend
    setTimeout(() => {
      inputRefs.current[0]?.focus();
      setResending(false);
    }, 500);
  };

  const filledCount = otp.filter(Boolean).length;

  return (
    <Box>
      <Typography
        style={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: "Inter",
          marginBottom: 8,
          textAlign: "center",
          color: "#0F172A",
        }}
      >
        Enter verification code
      </Typography>
      <Typography
        variant="body2"
        style={{
          textAlign: "center",
          marginBottom: 28,
          fontFamily: "Inter",
          color: "#64748B",
          lineHeight: 1.6,
        }}
      >
        Enter the code sent to{" "}
        <strong style={{ color: "#0F172A" }}>+{phone}</strong>
      </Typography>

      {/* OTP digit inputs */}
      <Box
        style={{ display: "flex", gap: 8, marginBottom: 8 }}
        onPaste={handlePaste}
      >
        {otp.map((val, index) => (
          <TextField
            key={index}
            inputRef={(el: HTMLInputElement | null) => { inputRefs.current[index] = el; }}
            value={val}
            autoFocus={index === 0}
            onChange={(e) => handleInput(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            variant="outlined"
            inputProps={{
              maxLength: 1,
              inputMode: "numeric" as any,
              pattern: "[0-9]*",
              autoComplete: "one-time-code",
              "aria-label": `Digit ${index + 1}`,
              style: {
                textAlign: "center",
                padding: "14px 0",
                fontSize: 22,
                fontWeight: 700,
                fontFamily: "Inter",
              },
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
        ))}
      </Box>

      {/* Error */}
      {otpError ? (
        <Typography
          variant="caption"
          style={{
            color: "#EF4444",
            display: "block",
            marginBottom: 12,
            textAlign: "center",
            fontFamily: "Inter",
          }}
        >
          {otpError}
        </Typography>
      ) : (
        <Box style={{ height: 20, marginBottom: 12 }} />
      )}

      {/* Resend row */}
      <Box style={{ textAlign: "center", marginBottom: 24 }}>
        {canResend ? (
          <Button
            size="small"
            disabled={resending}
            onClick={handleResend}
            style={{
              textTransform: "none",
              color: "#000",
              fontFamily: "Inter",
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {resending ? "Sending…" : "Resend code"}
          </Button>
        ) : (
          <Typography
            variant="caption"
            style={{ color: "#94A3B8", fontFamily: "Inter" }}
          >
            Resend code in{" "}
            <strong style={{ color: "#64748B" }}>{countdown}s</strong>
          </Typography>
        )}
      </Box>

      <Button
        variant="contained"
        fullWidth
        disabled={filledCount < 4 || loading}
        onClick={onContinue}
        style={{
          backgroundColor: "#000",
          color: "#fff",
          borderRadius: 24,
          textTransform: "none",
          padding: "13px 0",
          fontSize: 16,
          fontFamily: "Inter",
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        {loading ? (
          <CircularProgress size={22} style={{ color: "#fff" }} />
        ) : (
          "Continue"
        )}
      </Button>

      <Button
        fullWidth
        onClick={onChangePhone}
        style={{
          borderRadius: 24,
          textTransform: "none",
          color: "#64748B",
          fontSize: 14,
          fontFamily: "Inter",
        }}
      >
        Change phone number
      </Button>
    </Box>
  );
};

export default StepOtp;
// Customizable Area End
